import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveAssetUrl } from '../config';
import { isPlaceholderColor } from '../utils/placeholderColor';

const formatKES = (amount) =>
    'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

const TYPE_LABELS = {
    charger: 'Chargers',
    cable: 'Cables',
    cover: 'Cases',
    audio: 'Audio',
    screen_protector: 'Screen Protectors',
    other: 'Other',
};

function resolveImage(image) {
    if (!image) return null;
    return resolveAssetUrl(image);
}

function AccessoryModalItem({ item, onAddToCart, onClose }) {
    const variants = item.variants || [];

    const initVar =
        variants.find((v) => v._id?.toString() === item.variantId?.toString()) ||
        variants[0];

    const initAttrs = {};
    for (const attr of initVar?.attributes || []) {
        if (attr.slug && attr.valueId) initAttrs[attr.slug] = attr.valueId;
    }

    const [selectedColorName, setSelectedColorName] = useState(
        initVar?.color?.name || null
    );
    const [selectedAttrs, setSelectedAttrs] = useState(initAttrs);
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState(false);

    // Unique colors across all variants
    const uniqueColors = useMemo(() => {
        const seen = new Map();
        for (const v of variants) {
            if (v.color && !isPlaceholderColor(v.color) && !seen.has(v.color.name)) seen.set(v.color.name, v.color);
        }
        return [...seen.values()];
    }, [variants]);

    // Attribute options scoped to the selected color
    const attributeOptions = useMemo(() => {
        const colorVars = selectedColorName
            ? variants.filter((v) => v.color?.name === selectedColorName)
            : variants;
        const attrMap = new Map();
        for (const v of colorVars) {
            for (const attr of v.attributes || []) {
                if (!attrMap.has(attr.slug)) {
                    attrMap.set(attr.slug, {
                        name: attr.name,
                        slug: attr.slug,
                        values: new Map(),
                    });
                }
                attrMap
                    .get(attr.slug)
                    .values.set(attr.value, { value: attr.value, valueId: attr.valueId });
            }
        }
        return [...attrMap.values()].map((a) => ({
            ...a,
            values: [...a.values.values()],
        }));
    }, [variants, selectedColorName]);

    // Resolve the active variant from color + attribute selections
    const selectedVariant = useMemo(() => {
        if (!variants.length) return null;
        let filtered = variants;
        if (selectedColorName)
            filtered = filtered.filter((v) => v.color?.name === selectedColorName);
        for (const [slug, valueId] of Object.entries(selectedAttrs)) {
            filtered = filtered.filter((v) =>
                v.attributes.some((a) => a.slug === slug && a.valueId === valueId)
            );
        }
        return filtered.find((v) => v.inStock) || filtered[0] || null;
    }, [variants, selectedColorName, selectedAttrs]);

    const handleColorSelect = (colorName) => {
        const colorVars = variants.filter((v) => v.color?.name === colorName);
        const firstVar = colorVars.find((v) => v.inStock) || colorVars[0];
        const newAttrs = {};
        for (const attr of firstVar?.attributes || []) {
            if (attr.slug && attr.valueId) newAttrs[attr.slug] = attr.valueId;
        }
        setSelectedColorName(colorName);
        setSelectedAttrs(newAttrs);
        setAdded(false);
    };

    const handleAttrSelect = (slug, valueId) => {
        setSelectedAttrs((prev) => ({ ...prev, [slug]: valueId }));
        setAdded(false);
    };

    const handleAdd = async () => {
        const varId = selectedVariant?._id || item.variantId;
        if (!varId || adding || added) return;
        setAdding(true);
        try {
            await onAddToCart(varId, 1);
            setAdded(true);
        } finally {
            setAdding(false);
        }
    };

    const displayPrice =
        selectedVariant?.discountedPrice ?? selectedVariant?.price ?? item.price;
    const originalPrice = selectedVariant?.price ?? item.price;
    const hasDiscount = selectedVariant
        ? selectedVariant.discountedPrice != null &&
          selectedVariant.discountedPrice < selectedVariant.price
        : item.discountedPrice != null && item.discountedPrice < item.price;
    const imgSrc = resolveImage(selectedVariant?.image || item.image);
    const inStock = selectedVariant?.inStock ?? item.inStock;

    return (
        <div className="flex gap-3 p-3 rounded-xl border border-border hover:border-primary/30 transition-colors">
            {/* Image — updates on variant selection */}
            <div className="w-14 h-14 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                {imgSrc ? (
                    <img
                        src={imgSrc}
                        alt={item.name}
                        className="w-full h-full object-contain p-1"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-muted-foreground/40 text-xl"></i>
                    </div>
                )}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
                <Link
                    to={`/product/${item.slug}`}
                    onClick={onClose}
                    className="text-sm font-semibold text-foreground hover:text-primary transition-colors line-clamp-1"
                >
                    {item.name}
                </Link>

                {/* Color swatches */}
                {uniqueColors.length > 1 && (
                    <div className="flex gap-1 flex-wrap mt-1">
                        {uniqueColors.map((color) => (
                            <button
                                key={color.name}
                                title={color.name}
                                onClick={() => handleColorSelect(color.name)}
                                className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 transition-all ${
                                    selectedColorName === color.name
                                        ? 'border-primary scale-110'
                                        : 'border-border hover:border-primary/60'
                                }`}
                                style={{ backgroundColor: color.hexCode || '#6b7280' }}
                            />
                        ))}
                    </div>
                )}

                {/* Other attribute pills */}
                {attributeOptions.map((attr) =>
                    attr.values.length > 1 ? (
                        <div key={attr.slug} className="flex gap-1 flex-wrap mt-1">
                            {attr.values.map((val) => (
                                <button
                                    key={val.value}
                                    onClick={() => handleAttrSelect(attr.slug, val.valueId)}
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                                        selectedAttrs[attr.slug] === val.valueId
                                            ? 'bg-primary text-white border-primary'
                                            : 'border-border text-muted-foreground hover:border-primary/60'
                                    }`}
                                >
                                    {val.value}
                                </button>
                            ))}
                        </div>
                    ) : null
                )}

                {/* Price */}
                <div className="mt-1">
                    {hasDiscount ? (
                        <span className="flex items-baseline gap-1">
                            <span className="text-sm font-bold text-primary">
                                {formatKES(displayPrice)}
                            </span>
                            <span className="text-[10px] text-muted-foreground line-through">
                                {formatKES(originalPrice)}
                            </span>
                        </span>
                    ) : (
                        <span className="text-sm font-bold text-primary">
                            {formatKES(displayPrice)}
                        </span>
                    )}
                </div>
            </div>

            {/* Add button */}
            <button
                onClick={handleAdd}
                disabled={adding || added || !inStock}
                className={`flex-shrink-0 self-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    added
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : 'bg-primary text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
            >
                {!inStock ? (
                    'Out of stock'
                ) : adding ? (
                    <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
                ) : added ? (
                    <span className="flex items-center gap-1">
                        <i className="ri-check-line"></i> Added
                    </span>
                ) : (
                    <span className="flex items-center gap-1">
                        <i className="ri-add-line"></i> Add
                    </span>
                )}
            </button>
        </div>
    );
}

/**
 * Modal shown after adding a product to the cart. Lists compatible accessories
 * grouped by type (tabs). Each row shows color swatches and attribute pills so
 * the user can pick a variant before adding; clicking the name navigates to the
 * full detail page.
 */
export default function AccessoriesModal({
    items,
    productName,
    onAddToCart,
    onClose,
    onGoToCart,
}) {
    const [activeTab, setActiveTab] = useState('all');

    const tabs = useMemo(() => {
        const present = [
            ...new Set((items || []).map((i) => i.accessoryType || 'other')),
        ];
        return ['all', ...present];
    }, [items]);

    const visibleItems = useMemo(() => {
        if (activeTab === 'all') return items || [];
        return (items || []).filter(
            (i) => (i.accessoryType || 'other') === activeTab
        );
    }, [items, activeTab]);

    if (!items || items.length === 0) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.45)' }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
                    <div>
                        <h2 className="text-base font-bold text-foreground">
                            Compatible Accessories
                        </h2>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {productName
                                ? `Add accessories for ${productName}`
                                : 'Add compatible accessories to your order'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground transition-colors"
                    >
                        <i className="ri-close-line text-lg"></i>
                    </button>
                </div>

                {/* Type tabs */}
                {tabs.length > 2 && (
                    <div className="flex gap-2 px-6 pt-4 overflow-x-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                                    activeTab === tab
                                        ? 'bg-primary text-white'
                                        : 'bg-muted text-muted-foreground hover:bg-primary/10'
                                }`}
                            >
                                {tab === 'all' ? 'All' : TYPE_LABELS[tab] || tab}
                            </button>
                        ))}
                    </div>
                )}

                {/* Items list */}
                <div className="px-6 py-4 flex flex-col gap-3 max-h-96 overflow-y-auto">
                    {console.log(visibleItems)}
                    {visibleItems.map((item) => (
                        <>
                        <AccessoryModalItem
                            key={item._id || item.variantId}
                            item={item}
                            onAddToCart={onAddToCart}
                            onClose={onClose}
                            />
                            </>
                    ))}
                </div>

                {/* Footer */}
                <div className="flex gap-3 px-6 pb-5 pt-3 border-t border-border">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 border border-border rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
                    >
                        Continue Shopping
                    </button>
                    <button
                        onClick={onGoToCart}
                        className="flex-1 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:brightness-110 transition-all"
                    >
                        Go to Cart
                    </button>
                </div>
            </div>
        </div>
    );
}
